/**
 * Quiz Offline Storage
 * Enhanced offline support using IndexedDB for larger, more reliable storage
 * Falls back to localStorage if IndexedDB is not available
 */

import logger from './logger';

const DB_NAME = 'faithinsight_quiz_offline';
const DB_VERSION = 1;
const STORE_NAME = 'failed_saves';

class QuizOfflineStorage {
  constructor() {
    this.db = null;
    this.isIndexedDBAvailable = false;
    this.initPromise = null;
  }

  /**
   * Initialize IndexedDB
   * @returns {Promise<boolean>} Whether IndexedDB was successfully initialized
   */
  async init() {
    // Return existing promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._initDB();
    return this.initPromise;
  }

  async _initDB() {
    if (typeof window === 'undefined' || !window.indexedDB) {
      logger.debug('IndexedDB not available, falling back to localStorage');
      this.isIndexedDBAvailable = false;
      return false;
    }

    try {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
          logger.warn('IndexedDB open error, falling back to localStorage', { error: event.target.error });
          this.isIndexedDBAvailable = false;
          resolve(false);
        };

        request.onsuccess = (event) => {
          this.db = event.target.result;
          this.isIndexedDBAvailable = true;
          logger.debug('IndexedDB initialized successfully');
          resolve(true);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;

          // Create object store for failed saves
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
            
            // Create indexes for efficient querying
            store.createIndex('attemptId', 'attemptId', { unique: false });
            store.createIndex('questionId', 'questionId', { unique: false });
            store.createIndex('timestamp', 'timestamp', { unique: false });
            store.createIndex('attemptQuestion', ['attemptId', 'questionId'], { unique: false });
            
            logger.debug('IndexedDB object store created');
          }
        };
      });
    } catch (error) {
      logger.warn('IndexedDB initialization failed, falling back to localStorage', { error });
      this.isIndexedDBAvailable = false;
      return false;
    }
  }

  /**
   * Save a failed save to offline storage
   * @param {string} attemptId - Quiz attempt ID
   * @param {number} questionId - Question ID
   * @param {object} response - Response data
   * @param {object} error - Error that caused the save to fail
   * @returns {Promise<boolean>} Whether the save was successful
   */
  async saveFailedSave(attemptId, questionId, response, error) {
    await this.init();

    const saveData = {
      attemptId,
      questionId,
      response,
      error: error?.message || String(error),
      timestamp: Date.now(),
      retryCount: 0
    };

    if (this.isIndexedDBAvailable) {
      return this._saveToIndexedDB(saveData);
    } else {
      return this._saveToLocalStorage(attemptId, saveData);
    }
  }

  async _saveToIndexedDB(saveData) {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        // Check if we already have a save for this attempt/question
        const index = store.index('attemptQuestion');
        const getRequest = index.get([saveData.attemptId, saveData.questionId]);

        getRequest.onsuccess = (event) => {
          const existing = event.target.result;
          
          if (existing) {
            // Update existing record
            saveData.id = existing.id;
            saveData.retryCount = existing.retryCount;
          }

          const putRequest = store.put(saveData);
          
          putRequest.onsuccess = () => {
            logger.debug('Saved to IndexedDB', { attemptId: saveData.attemptId, questionId: saveData.questionId });
            resolve(true);
          };
          
          putRequest.onerror = (event) => {
            logger.error('Failed to save to IndexedDB', { error: event.target.error });
            resolve(false);
          };
        };

        getRequest.onerror = (event) => {
          // If get fails, try to add new record
          const addRequest = store.add(saveData);
          
          addRequest.onsuccess = () => {
            logger.debug('Added to IndexedDB', { attemptId: saveData.attemptId, questionId: saveData.questionId });
            resolve(true);
          };
          
          addRequest.onerror = (event) => {
            logger.error('Failed to add to IndexedDB', { error: event.target.error });
            resolve(false);
          };
        };
      });
    } catch (error) {
      logger.error('IndexedDB save error', { error });
      return false;
    }
  }

  _saveToLocalStorage(attemptId, saveData) {
    try {
      const key = `quiz_failed_saves_${attemptId}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      
      // Find and update or add
      const existingIndex = existing.findIndex(
        item => item.questionId === saveData.questionId
      );
      
      if (existingIndex >= 0) {
        existing[existingIndex] = saveData;
      } else {
        existing.push(saveData);
      }
      
      localStorage.setItem(key, JSON.stringify(existing));
      logger.debug('Saved to localStorage', { attemptId, questionId: saveData.questionId });
      return true;
    } catch (error) {
      logger.error('localStorage save error', { error });
      return false;
    }
  }

  /**
   * Get all failed saves for an attempt
   * @param {string} attemptId - Quiz attempt ID
   * @returns {Promise<Array>} Array of failed saves
   */
  async getFailedSaves(attemptId) {
    await this.init();

    if (this.isIndexedDBAvailable) {
      return this._getFromIndexedDB(attemptId);
    } else {
      return this._getFromLocalStorage(attemptId);
    }
  }

  async _getFromIndexedDB(attemptId) {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('attemptId');
        const request = index.getAll(attemptId);

        request.onsuccess = (event) => {
          resolve(event.target.result || []);
        };

        request.onerror = (event) => {
          logger.error('Failed to get from IndexedDB', { error: event.target.error });
          resolve([]);
        };
      });
    } catch (error) {
      logger.error('IndexedDB get error', { error });
      return [];
    }
  }

  _getFromLocalStorage(attemptId) {
    try {
      const key = `quiz_failed_saves_${attemptId}`;
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (error) {
      logger.error('localStorage get error', { error });
      return [];
    }
  }

  /**
   * Remove a failed save after successful retry
   * @param {string} attemptId - Quiz attempt ID
   * @param {number} questionId - Question ID
   * @returns {Promise<boolean>} Whether the removal was successful
   */
  async removeFailedSave(attemptId, questionId) {
    await this.init();

    if (this.isIndexedDBAvailable) {
      return this._removeFromIndexedDB(attemptId, questionId);
    } else {
      return this._removeFromLocalStorage(attemptId, questionId);
    }
  }

  async _removeFromIndexedDB(attemptId, questionId) {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('attemptQuestion');
        const request = index.openCursor([attemptId, questionId]);

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            cursor.delete();
            logger.debug('Removed from IndexedDB', { attemptId, questionId });
          }
          resolve(true);
        };

        request.onerror = (event) => {
          logger.error('Failed to remove from IndexedDB', { error: event.target.error });
          resolve(false);
        };
      });
    } catch (error) {
      logger.error('IndexedDB remove error', { error });
      return false;
    }
  }

  _removeFromLocalStorage(attemptId, questionId) {
    try {
      const key = `quiz_failed_saves_${attemptId}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const filtered = existing.filter(item => item.questionId !== questionId);
      
      if (filtered.length > 0) {
        localStorage.setItem(key, JSON.stringify(filtered));
      } else {
        localStorage.removeItem(key);
      }
      
      logger.debug('Removed from localStorage', { attemptId, questionId });
      return true;
    } catch (error) {
      logger.error('localStorage remove error', { error });
      return false;
    }
  }

  /**
   * Clear all failed saves for an attempt
   * @param {string} attemptId - Quiz attempt ID
   * @returns {Promise<boolean>} Whether the clear was successful
   */
  async clearAttempt(attemptId) {
    await this.init();

    if (this.isIndexedDBAvailable) {
      return this._clearFromIndexedDB(attemptId);
    } else {
      return this._clearFromLocalStorage(attemptId);
    }
  }

  async _clearFromIndexedDB(attemptId) {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('attemptId');
        const request = index.openCursor(attemptId);

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            logger.debug('Cleared attempt from IndexedDB', { attemptId });
            resolve(true);
          }
        };

        request.onerror = (event) => {
          logger.error('Failed to clear from IndexedDB', { error: event.target.error });
          resolve(false);
        };
      });
    } catch (error) {
      logger.error('IndexedDB clear error', { error });
      return false;
    }
  }

  _clearFromLocalStorage(attemptId) {
    try {
      const key = `quiz_failed_saves_${attemptId}`;
      localStorage.removeItem(key);
      logger.debug('Cleared attempt from localStorage', { attemptId });
      return true;
    } catch (error) {
      logger.error('localStorage clear error', { error });
      return false;
    }
  }

  /**
   * Increment retry count for a failed save
   * @param {string} attemptId - Quiz attempt ID
   * @param {number} questionId - Question ID
   * @returns {Promise<number>} New retry count
   */
  async incrementRetryCount(attemptId, questionId) {
    await this.init();

    if (this.isIndexedDBAvailable) {
      return this._incrementRetryInIndexedDB(attemptId, questionId);
    } else {
      return this._incrementRetryInLocalStorage(attemptId, questionId);
    }
  }

  async _incrementRetryInIndexedDB(attemptId, questionId) {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('attemptQuestion');
        const request = index.get([attemptId, questionId]);

        request.onsuccess = (event) => {
          const existing = event.target.result;
          if (existing) {
            existing.retryCount = (existing.retryCount || 0) + 1;
            store.put(existing);
            resolve(existing.retryCount);
          } else {
            resolve(0);
          }
        };

        request.onerror = (event) => {
          logger.error('Failed to increment retry in IndexedDB', { error: event.target.error });
          resolve(0);
        };
      });
    } catch (error) {
      logger.error('IndexedDB increment error', { error });
      return 0;
    }
  }

  _incrementRetryInLocalStorage(attemptId, questionId) {
    try {
      const key = `quiz_failed_saves_${attemptId}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const item = existing.find(item => item.questionId === questionId);
      
      if (item) {
        item.retryCount = (item.retryCount || 0) + 1;
        localStorage.setItem(key, JSON.stringify(existing));
        return item.retryCount;
      }
      
      return 0;
    } catch (error) {
      logger.error('localStorage increment error', { error });
      return 0;
    }
  }

  /**
   * Clean up old failed saves (older than maxAge)
   * @param {number} maxAge - Maximum age in milliseconds (default: 24 hours)
   * @returns {Promise<number>} Number of items cleaned up
   */
  async cleanupOldSaves(maxAge = 24 * 60 * 60 * 1000) {
    await this.init();

    const cutoff = Date.now() - maxAge;

    if (this.isIndexedDBAvailable) {
      return this._cleanupIndexedDB(cutoff);
    } else {
      return this._cleanupLocalStorage(cutoff);
    }
  }

  async _cleanupIndexedDB(cutoff) {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('timestamp');
        const range = IDBKeyRange.upperBound(cutoff);
        const request = index.openCursor(range);
        let count = 0;

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            cursor.delete();
            count++;
            cursor.continue();
          } else {
            logger.debug('Cleaned up old saves from IndexedDB', { count });
            resolve(count);
          }
        };

        request.onerror = (event) => {
          logger.error('Failed to cleanup IndexedDB', { error: event.target.error });
          resolve(0);
        };
      });
    } catch (error) {
      logger.error('IndexedDB cleanup error', { error });
      return 0;
    }
  }

  _cleanupLocalStorage(cutoff) {
    try {
      let totalCount = 0;
      
      // Find all quiz failed saves keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('quiz_failed_saves_')) {
          const items = JSON.parse(localStorage.getItem(key) || '[]');
          const filtered = items.filter(item => item.timestamp > cutoff);
          const removed = items.length - filtered.length;
          
          if (removed > 0) {
            totalCount += removed;
            if (filtered.length > 0) {
              localStorage.setItem(key, JSON.stringify(filtered));
            } else {
              localStorage.removeItem(key);
            }
          }
        }
      }
      
      logger.debug('Cleaned up old saves from localStorage', { count: totalCount });
      return totalCount;
    } catch (error) {
      logger.error('localStorage cleanup error', { error });
      return 0;
    }
  }

  /**
   * Get storage statistics
   * @returns {Promise<object>} Storage statistics
   */
  async getStats() {
    await this.init();

    if (this.isIndexedDBAvailable) {
      return this._getIndexedDBStats();
    } else {
      return this._getLocalStorageStats();
    }
  }

  async _getIndexedDBStats() {
    try {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const countRequest = store.count();

        countRequest.onsuccess = (event) => {
          resolve({
            storageType: 'IndexedDB',
            totalItems: event.target.result,
            isAvailable: true
          });
        };

        countRequest.onerror = (event) => {
          resolve({
            storageType: 'IndexedDB',
            totalItems: 0,
            isAvailable: true,
            error: event.target.error?.message
          });
        };
      });
    } catch (error) {
      return {
        storageType: 'IndexedDB',
        totalItems: 0,
        isAvailable: false,
        error: error.message
      };
    }
  }

  _getLocalStorageStats() {
    try {
      let totalItems = 0;
      let totalSize = 0;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('quiz_failed_saves_')) {
          const value = localStorage.getItem(key);
          const items = JSON.parse(value || '[]');
          totalItems += items.length;
          totalSize += value.length;
        }
      }
      
      return {
        storageType: 'localStorage',
        totalItems,
        totalSize,
        isAvailable: true
      };
    } catch (error) {
      return {
        storageType: 'localStorage',
        totalItems: 0,
        isAvailable: false,
        error: error.message
      };
    }
  }
}

// Create singleton instance
const quizOfflineStorage = new QuizOfflineStorage();

export default quizOfflineStorage;
